import React, { useState, useEffect } from 'react';

const formatarValor = (valor) => {
  if (!valor && valor !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(valor));
};

/**
 * Modal de histórico de pagamentos do fornecedor + análise inteligente.
 * Extraído de Planejamento_orcamentario.js sem mudança visual.
 *
 * Props:
 *  aberto: bool — exibe/oculta
 *  carregando: bool — spinner enquanto busca
 *  dados: objeto retornado por /api/historico-fornecedor/ (resp.dados) ou null
 *  onFechar: () => void
 *  onNegociar: () => void — clique em "Negociar contrato" (parent abre o modal de e-mail)
 */
const ModalHistoricoFornecedor = ({ aberto, carregando, dados, onFechar, onNegociar }) => {
  const [mesesExpandidos, setMesesExpandidos] = useState(new Set());

  useEffect(() => {
    if (aberto && dados?.historico?.length > 0) {
      setMesesExpandidos(new Set([dados.historico[0].mes_ano]));
    } else if (!aberto) {
      setMesesExpandidos(new Set());
    }
  }, [aberto, dados]);

  if (!aberto) return null;

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', zIndex: 1080 }}
      onClick={onFechar}
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">

          <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
            <div>
              <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                <i className="fas fa-history me-2"></i>
                Histórico — {dados?.fornecedor?.nome || '...'}
              </h5>
              {dados?.fornecedor?.cnpj_cpf && (
                <small style={{ color: '#6c757d' }}>{dados.fornecedor.cnpj_cpf}</small>
              )}
            </div>
            <button type="button" className="btn-close" onClick={onFechar}></button>
          </div>

          {carregando ? (
            <div className="modal-body text-center py-5">
              <div className="spinner-border text-dark mb-3"></div>
              <p className="text-muted">Buscando histórico...</p>
            </div>
          ) : dados ? (
            <>
              <div className="modal-header bg-light py-3">
                <div className="w-100">
                  <div className="row align-items-center">
                    <div className="col-md-5">
                      <div className="d-flex gap-2 flex-wrap">
                        {dados.fornecedor.email ? (
                          <button className="btn btn-primary btn-sm" onClick={onNegociar}>
                            <i className="fas fa-envelope me-1"></i>Negociar contrato
                          </button>
                        ) : (
                          <button className="btn btn-primary btn-sm" onClick={onNegociar} title="E-mail será solicitado">
                            <i className="fas fa-envelope me-1"></i>Negociar contrato
                          </button>
                        )}
                        {dados.fornecedor.whatsapp ? (
                          <a
                            href={`https://wa.me/${dados.fornecedor.whatsapp}?text=${encodeURIComponent(`Olá ${dados.fornecedor.nome}! Gostaríamos de conversar sobre uma revisão nos valores dos serviços prestados. Podemos agendar uma conversa?`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-success btn-sm"
                          >
                            <i className="fab fa-whatsapp me-1"></i>Contatar Fornecedor
                          </a>
                        ) : (
                          <button className="btn btn-outline-secondary btn-sm" disabled title="Telefone não cadastrado">
                            <i className="fab fa-whatsapp me-1"></i>Sem telefone
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="col-md-7">
                      <div className="d-flex gap-4 justify-content-end text-center">
                        <div>
                          <small className="text-muted d-block">Total Pago (histórico)</small>
                          <strong className="text-danger fs-6">{formatarValor(dados.total_geral)}</strong>
                        </div>
                        <div>
                          <small className="text-muted d-block">Meses com pagamento</small>
                          <strong className="fs-6">{dados.total_meses}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {dados.historico.length >= 1 && (() => {
                const hist = [...dados.historico].sort((a, b) => a.mes_ano.localeCompare(b.mes_ano));
                const totais = hist.map(m => parseFloat(m.total) || 0);
                const n = totais.length;

                const somaTotal = totais.reduce((s, v) => s + v, 0);
                const media = n > 0 ? somaTotal / n : 0;
                const valorAtual = totais[n - 1] || 0;

                const ultimos3 = totais.slice(-Math.min(3, n));
                const mediaRecente = ultimos3.reduce((s, v) => s + v, 0) / ultimos3.length;

                const temTendencia = n >= 3;
                const primeiros3 = totais.slice(0, Math.min(3, n));
                const mediaBase = primeiros3.reduce((s, v) => s + v, 0) / primeiros3.length;
                const tendencia = temTendencia && mediaBase > 0 ? ((mediaRecente - mediaBase) / mediaBase) * 100 : null;

                let valorSugerido, justificativa, corSugestao;
                if (tendencia === null) {
                  valorSugerido = media;
                  justificativa = `Histórico com ${n} mês${n > 1 ? 'es' : ''} de pagamento. Dados insuficientes para calcular tendência — sugerimos usar a média como referência.`;
                  corSugestao = '#6c757d';
                } else if (tendencia > 10) {
                  valorSugerido = media * 0.95;
                  justificativa = `Os pagamentos cresceram ${tendencia.toFixed(1)}% em relação ao início do histórico. Sugerimos negociar uma redução de ≈5% sobre a média histórica para equilibrar os custos.`;
                  corSugestao = '#c0392b';
                } else if (tendencia < -10) {
                  valorSugerido = mediaRecente;
                  justificativa = `Os pagamentos reduziram ${Math.abs(tendencia).toFixed(1)}% ao longo do tempo — tendência positiva. Sugerimos manter a média dos últimos 3 meses como referência.`;
                  corSugestao = '#021a0c';
                } else {
                  valorSugerido = media;
                  justificativa = `Os valores estão estáveis (variação de ${Math.abs(tendencia).toFixed(1)}%). Sugerimos usar a média histórica como referência para renovação do contrato.`;
                  corSugestao = '#2980b9';
                }

                const economiaAnual = (valorAtual - valorSugerido) * 12;
                const corTendencia = tendencia === null ? '#6c757d' : tendencia > 5 ? '#e74c3c' : tendencia < -5 ? '#27ae60' : '#2980b9';
                const setaTendencia = tendencia === null ? '—' : tendencia > 0 ? '▲' : tendencia < 0 ? '▼' : '●';
                const tendenciaTexto = tendencia === null ? 'Poucos dados' : `${setaTendencia} ${Math.abs(tendencia).toFixed(1)}%`;

                return (
                  <div style={{ margin: '16px 16px 0', borderRadius: '10px', border: '1px solid #dee2e6', overflow: 'hidden' }}>
                    <div style={{ backgroundColor: '#212529', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <i className="fas fa-robot" style={{ color: '#f39c12', fontSize: '18px' }}></i>
                      <span style={{ color: '#fff', fontWeight: 700, fontSize: '15px' }}>Análise Inteligente do Contrato</span>
                      <span style={{ color: '#adb5bd', fontSize: '12px' }}>— {n} {n === 1 ? 'mês' : 'meses'} de histórico</span>
                    </div>

                    <div style={{ display: 'flex', borderBottom: '1px solid #dee2e6', backgroundColor: '#fff' }}>
                      {[
                        { label: 'Último Mês Pago', valor: formatarValor(valorAtual), cor: '#e74c3c', extra: null },
                        { label: 'Média Histórica', valor: formatarValor(media), cor: '#2980b9', extra: null },
                        { label: n >= 3 ? 'Média Últ. 3 Meses' : `Média (${n} mês${n > 1 ? 'es' : ''})`, valor: formatarValor(mediaRecente), cor: '#8e44ad', extra: null },
                        { label: 'Tendência', valor: tendenciaTexto, cor: corTendencia, extra: tendencia === null ? 'mín. 3 meses' : null },
                      ].map((item, i) => (
                        <div key={i} style={{ flex: 1, textAlign: 'center', padding: '14px 12px', borderRight: i < 3 ? '1px solid #dee2e6' : 'none' }}>
                          <div style={{ color: '#6c757d', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '6px' }}>{item.label}</div>
                          <div style={{ color: item.cor, fontWeight: 700, fontSize: '16px' }}>{item.valor}</div>
                          {item.extra && <div style={{ color: '#adb5bd', fontSize: '10px', marginTop: '2px' }}>{item.extra}</div>}
                        </div>
                      ))}
                    </div>

                    <div style={{ backgroundColor: '#f8f9fa', padding: '16px 18px', display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                      <div style={{ backgroundColor: corSugestao, borderRadius: '10px', padding: '12px 16px', flexShrink: 0, textAlign: 'center', minWidth: '155px' }}>
                        <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>Valor Sugerido</div>
                        <div style={{ color: '#fff', fontWeight: 700, fontSize: '20px' }}>{formatarValor(valorSugerido)}</div>
                        {economiaAnual > 0 && (
                          <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '11px', marginTop: '4px' }}>
                            economia de {formatarValor(economiaAnual)}/ano
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ color: '#212529', fontWeight: 700, fontSize: '13px', marginBottom: '6px' }}>
                          <i className="fas fa-lightbulb me-2" style={{ color: '#f39c12' }}></i>Recomendação
                        </div>
                        <p style={{ color: '#495057', fontSize: '13px', margin: 0, lineHeight: '1.6' }}>{justificativa}</p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="modal-body p-0" style={{ marginTop: '16px' }}>
                {dados.historico.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="fas fa-inbox fa-3x mb-3 d-block"></i>
                    Nenhum pagamento encontrado para este fornecedor.
                  </div>
                ) : (
                  <table className="table table-hover mb-0">
                    <tbody>
                      {dados.historico.map((mes) => {
                        const expandido = mesesExpandidos.has(mes.mes_ano);
                        return (
                          <React.Fragment key={mes.mes_ano}>
                            <tr
                              style={{ backgroundColor: '#f1f3f5', cursor: 'pointer', borderLeft: '4px solid #495057' }}
                              onClick={() => setMesesExpandidos(prev => {
                                const next = new Set(prev);
                                next.has(mes.mes_ano) ? next.delete(mes.mes_ano) : next.add(mes.mes_ano);
                                return next;
                              })}
                            >
                              <td style={{ width: '40%' }}>
                                <strong style={{ color: '#212529', fontSize: '16px' }}>
                                  <i className={`fas fa-chevron-${expandido ? 'down' : 'right'} me-2`} style={{ fontSize: '11px' }}></i>
                                  {mes.mes_label}
                                </strong>
                                <small className="text-muted ms-2">({mes.lancamentos.length} lançamento{mes.lancamentos.length !== 1 ? 's' : ''})</small>
                              </td>
                              <td className="text-end" style={{ width: '30%' }}>
                                <strong className="text-danger">{formatarValor(mes.total)}</strong>
                              </td>
                              <td style={{ width: '30%' }}></td>
                            </tr>
                            {expandido && mes.lancamentos.map((lanc, i) => (
                              <tr key={i} style={{ backgroundColor: '#fff' }}>
                                <td style={{ paddingLeft: '36px' }}>
                                  <span className="text-muted" style={{ fontSize: '12px' }}>
                                    {lanc.nome_categoria || lanc.codigo_categoria}
                                  </span>
                                </td>
                                <td className="text-end">
                                  <strong className="text-danger" style={{ fontSize: '13px' }}>{formatarValor(lanc.valor)}</strong>
                                </td>
                                <td>
                                  <span className="text-muted" style={{ fontSize: '12px' }}>
                                    {lanc.data_vencimento ? new Date(lanc.data_vencimento).toLocaleDateString('pt-BR') : '—'}
                                    {lanc.numero_documento && ` · Doc: ${lanc.numero_documento}`}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : null}

          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onFechar}>
              <i className="fas fa-arrow-left me-2"></i>Voltar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalHistoricoFornecedor;
